import type { CSSProperties } from 'react';
import type { SessionTenant } from './session/session-provider';

interface BrandPalette {
  readonly accent: string;
  readonly accentStrong: string;
  readonly accentSoft: string;
  readonly glow: string;
  readonly canvas: string;
}

export interface AppBranding {
  readonly key: string;
  readonly tenantLabel: string;
  readonly mark: string;
  readonly palette: BrandPalette;
}

const DEFAULT_BRANDING: AppBranding = {
  key: 'event',
  tenantLabel: 'Event',
  mark: 'EV',
  palette: {
    accent: '#0f7a67',
    accentStrong: '#0d5d53',
    accentSoft: '#def4ef',
    glow: 'rgba(15, 122, 103, 0.18)',
    canvas: 'rgba(15, 122, 103, 0.08)',
  },
};

const EDO_BRANDING: AppBranding = {
  key: 'edo',
  tenantLabel: 'eDO',
  mark: 'eD',
  palette: {
    accent: '#0f766e',
    accentStrong: '#155e75',
    accentSoft: '#dbf5f0',
    glow: 'rgba(15, 118, 110, 0.18)',
    canvas: 'rgba(13, 148, 136, 0.08)',
  },
};

export function resolveBranding(
  tenant?: Pick<SessionTenant, 'code' | 'displayName'>,
): AppBranding {
  const tenantCode = tenant?.code.trim().toLowerCase() ?? '';
  if (tenantCode === 'edo') {
    return {
      ...EDO_BRANDING,
      tenantLabel: tenant?.displayName ?? EDO_BRANDING.tenantLabel,
      mark: initials(tenant?.displayName ?? EDO_BRANDING.tenantLabel),
    };
  }

  if (!tenant) {
    return DEFAULT_BRANDING;
  }

  return {
    ...DEFAULT_BRANDING,
    key: tenantCode || DEFAULT_BRANDING.key,
    tenantLabel: tenant.displayName,
    mark: initials(tenant.displayName || tenant.code || DEFAULT_BRANDING.tenantLabel),
  };
}

export function brandingStyle(branding: AppBranding): CSSProperties {
  return {
    '--brand-accent': branding.palette.accent,
    '--brand-accent-strong': branding.palette.accentStrong,
    '--brand-accent-soft': branding.palette.accentSoft,
    '--brand-glow': branding.palette.glow,
    '--brand-canvas': branding.palette.canvas,
  } as CSSProperties;
}

function initials(value: string): string {
  const chunks = value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .slice(0, 2);
  if (chunks.length === 0) return DEFAULT_BRANDING.mark;
  return chunks
    .map((chunk) => chunk[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2);
}
